import { Button } from '@chakra-ui/react'
import {
  MeetingStepFragment,
  Meeting_Step_Type_Enum,
  useUpdateMeetingStepMutation,
} from '@gql'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { EditIcon } from 'src/icons'
import { EditorHandle } from '../../editor'
import CollabEditor from '../../editor/components/CollabEditor'
import { MeetingContext } from '../contexts/MeetingContext'
import MeetingStepContentChecklist from './MeetingStepContentChecklist'
import MeetingStepContentIndicators from './MeetingStepContentIndicators'
import MeetingStepContentNextSteps from './MeetingStepContentNextSteps'
import MeetingStepContentTasks from './MeetingStepContentTasks'
import MeetingStepContentThreads from './MeetingStepContentThreads'
import { isThreadsStep } from './meetingStepThreadTypes'

interface Props {
  step: MeetingStepFragment
}

export default function MeetingStepContent({ step }: Props) {
  const { meeting, editable } = useContext(MeetingContext)!
  const { t } = useTranslation()
  const editorRef = useRef<EditorHandle>(null)
  const [forceEdit, setForceEdit] = useState(false)
  const [updateMeetingStep] = useUpdateMeetingStepMutation()

  // Disable force edit after having saved some notes
  useEffect(() => {
    if (forceEdit && step.notes) {
      setForceEdit(false)
    }
  }, [forceEdit, step.notes])

  // Disable force edit on blur if editor is empty
  const handleNotesBlur = useCallback(() => {
    if (editorRef.current?.isEmpty()) {
      setForceEdit(false)
    }
  }, [])

  // Update notes
  const handleNotesChange = useCallback(
    (notes: string) => {
      updateMeetingStep({
        variables: {
          id: step.id,
          values: { notes },
        },
      })
    },
    [forceEdit]
  )

  if (!meeting) return null

  // Checklist and Indicators lost their free notes with REU-04 (D25). They are
  // the two steps whose notes only ever held a copy of the role's text, made
  // once at creation; keeping the box under a live view would invite retyping
  // by hand what is already displayed above. What gets said in the room already
  // has its place — the meeting's own notes.
  const hasNotes =
    step.type !== Meeting_Step_Type_Enum.Checklist &&
    step.type !== Meeting_Step_Type_Enum.Indicators

  return (
    <>
      {isThreadsStep(step.type) && (
        <MeetingStepContentThreads step={step as any} />
      )}

      {step.type === Meeting_Step_Type_Enum.Tasks && (
        <MeetingStepContentTasks step={step as any} />
      )}

      {step.type === Meeting_Step_Type_Enum.NextSteps && (
        <MeetingStepContentNextSteps step={step as any} />
      )}

      {step.type === Meeting_Step_Type_Enum.Checklist && (
        <MeetingStepContentChecklist step={step as any} />
      )}

      {step.type === Meeting_Step_Type_Enum.Indicators && (
        <MeetingStepContentIndicators step={step as any} />
      )}

      {hasNotes &&
        (step.notes || forceEdit ? (
          <CollabEditor
            ref={editorRef}
            docId={`meeting-step-${step.id}`}
            value={step.notes}
            placeholder={t('MeetingStepContent.notesPlaceholder')}
            readOnly={!editable}
            autoFocus={forceEdit}
            saveEvery={10000}
            onSave={handleNotesChange}
            onBlur={handleNotesBlur}
          />
        ) : (
          editable && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<EditIcon size={18} />}
              onClick={() => setForceEdit(true)}
            >
              {t('MeetingStepContent.addNotes')}
            </Button>
          )
        ))}
    </>
  )
}
